/**
 * Recommendations API — Supabase PostgREST queries.
 *
 * Schema (see migrations/054_recommendations.sql):
 *   recommendations(id, service_id, author_id, testimonial, status,
 *                   created_at, updated_at)
 *
 * This replaces reviews. See 6_REVIEWS_REQUIREMENT.md for why. The rules worth
 * knowing before changing anything here:
 *
 *   * There is no rating. Not hidden, not optional — the column does not exist.
 *   * There is no subject_type. `service_id` is a foreign key, so a member
 *     cannot be the subject of one.
 *   * Nothing is published by this file. Rows arrive 'pending' because the RLS
 *     INSERT policy demands it, and only the admin queue can promote them. The
 *     old reviews API wrote `status: "approved"` here and that is exactly the
 *     bug 054 closed in the database rather than in code.
 *   * Nothing negative belongs in this table. A bad experience is a report.
 */

import supabase from "../config/supabase";
import { invokeFunction } from "./functionErrors";

const AUTHOR_SELECT =
  "author:users!author_id(id, first_name, last_name, profile_image_url)";

const recommendationsApi = {
  /**
   * Approved recommendations for a listing, newest first.
   *
   * RLS returns the caller's own pending row as well, so the caller is asked for
   * explicitly rather than filtered here — see `getMine`.
   */
  async list({ service_id, limit } = {}) {
    if (!service_id) return { success: false, error: "Missing service" };

    let query = supabase
      .from("recommendations")
      .select(`*, ${AUTHOR_SELECT}`)
      .eq("service_id", service_id)
      .eq("status", "approved")
      .order("created_at", { ascending: false });

    if (limit) query = query.limit(limit);

    const { data, error } = await query;
    if (error) return { success: false, error: error.message };
    return { success: true, data: data || [] };
  },

  /**
   * Only those with words. The count comes from `services.recommendation_count`,
   * so a screen showing testimonials does not need to fetch the silent vouches
   * to know the total.
   */
  async listTestimonials({ service_id, limit } = {}) {
    const result = await this.list({ service_id, limit });
    if (!result.success) return result;
    return {
      success: true,
      data: result.data.filter((row) => row.testimonial),
    };
  },

  /** The caller's own row for a listing, whatever state it is in. */
  async getMine({ service_id } = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };
    if (!service_id) return { success: false, error: "Missing service" };

    const { data, error } = await supabase
      .from("recommendations")
      .select("*")
      .eq("service_id", service_id)
      .eq("author_id", user.id)
      .maybeSingle();

    if (error) return { success: false, error: error.message };
    return { success: true, data: data || null };
  },

  /**
   * Vouch for a listing, with or without words.
   *
   * Upserts, because the unique constraint is one per member per business and a
   * member changing their wording should not hit a duplicate-key error. Status
   * is not set: the column defaults to 'pending' and the INSERT policy would
   * reject anything else, so sending it would only invite someone to try.
   */
  async create({ service_id, testimonial } = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };
    if (!service_id) return { success: false, error: "Missing service" };

    const words = (testimonial || "").trim();
    if (!words) return {success: false, error: "Please write a few words about what they did well."};

    const { data, error } = await supabase
      .from("recommendations")
      .upsert(
        { service_id, author_id: user.id, testimonial: words },
        { onConflict: "author_id,service_id" },
      )
      .select()
      .single();

    if (error) return { success: false, error: error.message };

    // Scanned after the row exists so the agent has something to read, and so a
    // scan failure cannot lose the recommendation. An unscanned row is still
    // pending and still invisible, so the worst case is an admin seeing it
    // without a flag rather than a member's vouch disappearing.
    const scan = await this.requestScan(data.id);

    return { success: true, data, scanned: scan.success };
  },

  /**
   * Take a published recommendation down. Moderators only, per 054's
   * recommendations_update_moderator policy.
   *
   * Reviews routed this through the Approvals queue instead, because a review
   * could be defamatory and the decision to publish or delete wanted a record.
   * A recommendation is positive-only, so the judgement is simpler and waiting
   * is the wrong default: it comes down now. 'removed' rather than deleted keeps
   * the slot filled, so the member cannot simply vouch again with the same
   * wording, and the count trigger from 054 keeps the listing in step.
   */
  async moderatorRemove(id) {
    const { error } = await supabase
      .from("recommendations")
      .update({ status: "removed" })
      .eq("id", id);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  /** Withdraw your own vouch. */
  async remove(id) {
    const { error } = await supabase.from("recommendations").delete().eq("id", id);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  /**
   * Correct the wording of a testimonial while it sits in the admin queue.
   *
   * This is what makes a "backhanded" flag useful rather than just a rejection:
   * the recommendation is usually genuine and the member has simply wandered into
   * a comparison, so an admin can lift the comparison out and publish the rest.
   *
   * Two rows hold that wording and both have to move, which is why this is one
   * call rather than an update at the call site:
   *
   *   recommendations.testimonial       what members read once it is published
   *   moderation_queue.content_snapshot what the queue card itself displays
   *
   * Saving only the recommendation publishes correctly but leaves the admin
   * reading the text they have just rewritten. Saving only the snapshot looks
   * right in the queue and then publishes the original wording. `reviews.js` has
   * the same note on `editQueuedComment`, and it was written for that exact bug.
   *
   * Both writes go through the moderator's own policies, not the service role's:
   * `recommendations_update_moderator` from 054 and `moderation_update`.
   */
  async editQueuedTestimonial(recommendationId, moderationId, testimonial) {
    const words = (testimonial || "").trim();
    if (!words) {
      return { success: false, error: "A testimonial cannot be blanked. Reject it instead." };
    }

    const { error: recError } = await supabase
      .from("recommendations")
      .update({ testimonial: words })
      .eq("id", recommendationId);
    if (recError) return { success: false, error: recError.message };

    // A recommendation reaching the queue screen without a queue row would be a
    // bug elsewhere, but the correction has already landed, so this reports
    // success rather than inventing a failure.
    if (!moderationId) return { success: true };

    const { data: queued, error: readError } = await supabase
      .from("moderation_queue")
      .select("content_snapshot")
      .eq("id", moderationId)
      .single();
    if (readError) return { success: false, error: readError.message };

    // Merged, not replaced: the snapshot also carries the business name, the
    // author and the service id, and the card needs all of them.
    const { error: writeError } = await supabase
      .from("moderation_queue")
      .update({
        content_snapshot: { ...(queued?.content_snapshot || {}), testimonial: words },
      })
      .eq("id", moderationId);
    if (writeError) return { success: false, error: writeError.message };

    return { success: true };
  },

  /**
   * Hand a recommendation to the AI agent, which labels it and puts it in the
   * admin queue. The agent never decides — see the ai-moderation function.
   */
  async requestScan(id) {
    return invokeFunction(
      supabase,
      "ai-moderation",
      { body: { item_id: id, item_type: "recommendation" } },
      "Your recommendation was saved but could not be checked yet. An admin will still see it.",
    );
  },
};

export default recommendationsApi;
export { recommendationsApi };
